import { getCurrentContextuserIdDetails } from './components-examples/Admin/AccountUtility';

/**
 * MasterDataFetcher: Unified utility for fetching flows and groups (global & org)
 */

const inFlightRequests = new Map();

const trackInFlight = (key, promise) => {
    inFlightRequests.set(key, promise);
    return promise.finally(() => {
        inFlightRequests.delete(key);
    });
};

export const fetchGlobalData = (dispatch, api, props) => {
    if (!api) return Promise.resolve();

    const { learnPracticeFlows, buildPracticeFlows, masterGroupMapper } = props;

    const learnKey = 'GLOBAL_LEARN';
    const learnP = inFlightRequests.get(learnKey) || (learnPracticeFlows && (learnPracticeFlows.isLoading || (learnPracticeFlows.fetched && learnPracticeFlows.lastOrgId === 'GLOBAL')))
        ? (inFlightRequests.get(learnKey) || Promise.resolve())
        : trackInFlight(learnKey, Promise.resolve(dispatch(api.getLearnFlowsRSAA())));

    const buildKey = 'GLOBAL_BUILD';
    const buildP = inFlightRequests.get(buildKey) || (buildPracticeFlows && (buildPracticeFlows.isLoading || (buildPracticeFlows.fetched && buildPracticeFlows.lastOrgId === 'GLOBAL')))
        ? (inFlightRequests.get(buildKey) || Promise.resolve())
        : trackInFlight(buildKey, Promise.resolve(dispatch(api.getBuildFlowsRSAA())));

    const groupKey = 'GLOBAL_GROUPS';
    const groupP = inFlightRequests.get(groupKey) || (masterGroupMapper && (masterGroupMapper.isLoading || (masterGroupMapper.fetched && masterGroupMapper.lastOrgId === 'GLOBAL')))
        ? (inFlightRequests.get(groupKey) || Promise.resolve())
        : trackInFlight(groupKey, Promise.resolve(dispatch(api.getProjectGroupsRSAA())));

    return Promise.all([learnP, buildP, groupP]);
};

// Flows-only variant for screens that don't need groups
export const fetchGlobalFlows = (dispatch, api, props) => {
    if (!api) return Promise.resolve();

    const { learnPracticeFlows, buildPracticeFlows } = props;

    const learnKey = 'GLOBAL_LEARN';
    const learnP = inFlightRequests.get(learnKey) || (learnPracticeFlows && (learnPracticeFlows.isLoading || learnPracticeFlows.fetched))
        ? (inFlightRequests.get(learnKey) || Promise.resolve())
        : trackInFlight(learnKey, Promise.resolve(dispatch(api.getLearnFlowsRSAA())));

    const buildKey = 'GLOBAL_BUILD';
    const buildP = inFlightRequests.get(buildKey) || (buildPracticeFlows && (buildPracticeFlows.isLoading || buildPracticeFlows.fetched))
        ? (inFlightRequests.get(buildKey) || Promise.resolve())
        : trackInFlight(buildKey, Promise.resolve(dispatch(api.getBuildFlowsRSAA())));

    return Promise.all([learnP, buildP]);
};

export const fetchOrgData = (dispatch, api, orgId, props) => {
    if (!api || !orgId || orgId === 'undefined' || orgId === 'null') return Promise.resolve();

    const { learnPracticeFlows, buildPracticeFlows, masterGroupMapper } = props;

    const groupKey = `ORG_GROUPS_${orgId}`;
    const groupP = inFlightRequests.get(groupKey) || (masterGroupMapper && (masterGroupMapper.isLoading || (masterGroupMapper.fetched && masterGroupMapper.lastOrgId === orgId)))
        ? (inFlightRequests.get(groupKey) || Promise.resolve())
        : trackInFlight(groupKey, Promise.resolve(dispatch(api.getOrgProjectGroupsRSAA(orgId))));

    const learnKey = `ORG_LEARN_${orgId}`;
    const learnP = inFlightRequests.get(learnKey) || (learnPracticeFlows && (learnPracticeFlows.isLoading || (learnPracticeFlows.fetched && learnPracticeFlows.lastOrgId === orgId)))
        ? (inFlightRequests.get(learnKey) || Promise.resolve())
        : trackInFlight(learnKey, Promise.resolve(dispatch(api.getOrgLearnFlowsRSAA(orgId))));

    const buildKey = `ORG_BUILD_${orgId}`;
    const buildP = inFlightRequests.get(buildKey) || (buildPracticeFlows && (buildPracticeFlows.isLoading || (buildPracticeFlows.fetched && buildPracticeFlows.lastOrgId === orgId)))
        ? (inFlightRequests.get(buildKey) || Promise.resolve())
        : trackInFlight(buildKey, Promise.resolve(dispatch(api.getOrgBuildFlowsRSAA(orgId))));

    return Promise.all([groupP, learnP, buildP]);
};

export const fetchOrgFlows = (dispatch, api, orgId, props) => {
    if (!api || !orgId || orgId === 'undefined' || orgId === 'null') return Promise.resolve();

    const { learnPracticeFlows, buildPracticeFlows } = props;

    const learnKey = `ORG_LEARN_${orgId}`;
    const learnP = inFlightRequests.get(learnKey) || (learnPracticeFlows && (learnPracticeFlows.isLoading || (learnPracticeFlows.fetched && learnPracticeFlows.lastOrgId === orgId)))
        ? (inFlightRequests.get(learnKey) || Promise.resolve())
        : trackInFlight(learnKey, Promise.resolve(dispatch(api.getOrgLearnFlowsRSAA(orgId))));

    const buildKey = `ORG_BUILD_${orgId}`;
    const buildP = inFlightRequests.get(buildKey) || (buildPracticeFlows && (buildPracticeFlows.isLoading || (buildPracticeFlows.fetched && buildPracticeFlows.lastOrgId === orgId)))
        ? (inFlightRequests.get(buildKey) || Promise.resolve())
        : trackInFlight(buildKey, Promise.resolve(dispatch(api.getOrgBuildFlowsRSAA(orgId))));

    return Promise.all([learnP, buildP]);
};

export const isAllDataFetched = (props, cUser) => {
    const { learnPracticeFlows, buildPracticeFlows, masterGroupMapper } = props;

    const learnFetched = learnPracticeFlows && learnPracticeFlows.fetched;
    const buildFetched = buildPracticeFlows && buildPracticeFlows.fetched;
    const groupsFetched = masterGroupMapper && masterGroupMapper.fetched;

    const globalFetched = learnFetched && buildFetched && groupsFetched;

    if (cUser && cUser.orgId && cUser.orgId !== 'undefined' && cUser.orgId !== 'null') {
        const orgMatch = (
            learnPracticeFlows.lastOrgId === cUser.orgId &&
            buildPracticeFlows.lastOrgId === cUser.orgId &&
            masterGroupMapper.lastOrgId === cUser.orgId
        );
        return globalFetched && orgMatch;
    }

    return globalFetched;
};

export const isFlowsFetched = (props, cUser) => {
    const { learnPracticeFlows, buildPracticeFlows } = props;

    const learnFetched = learnPracticeFlows && learnPracticeFlows.fetched;
    const buildFetched = buildPracticeFlows && buildPracticeFlows.fetched;

    const globalFetched = learnFetched && buildFetched;

    if (cUser && cUser.orgId && cUser.orgId !== 'undefined' && cUser.orgId !== 'null') {
        const orgMatch = (
            learnPracticeFlows.lastOrgId === cUser.orgId &&
            buildPracticeFlows.lastOrgId === cUser.orgId
        );
        return globalFetched && orgMatch;
    }

    return globalFetched;
};

export const ensureDataLoaded = (dispatch, api, cUser, props) => {
    if (!api || !cUser || !props) return Promise.resolve();

    const { learnPracticeFlows, buildPracticeFlows, masterGroupMapper } = props;

    const needsGlobal = (
        (!learnPracticeFlows || (!learnPracticeFlows.fetched && !learnPracticeFlows.isLoading)) ||
        (!buildPracticeFlows || (!buildPracticeFlows.fetched && !buildPracticeFlows.isLoading)) ||
        (!masterGroupMapper || (!masterGroupMapper.fetched && !masterGroupMapper.isLoading))
    );

    const isGlobalInFlight = inFlightRequests.has('GLOBAL_LEARN') || inFlightRequests.has('GLOBAL_BUILD') || inFlightRequests.has('GLOBAL_GROUPS');

    if (needsGlobal || isGlobalInFlight) {
        return fetchGlobalData(dispatch, api, props).then(() => {
            if (cUser.orgId && cUser.orgId !== 'undefined' && cUser.orgId !== 'null') {
                // If we just fetched Global, we MUST fetch Org data to restore org context,
                // even if props.lastOrgId currently matches (as it may be stale).
                // We pass an empty object for props to bypass the stale "already matched" check.
                return fetchOrgData(dispatch, api, cUser.orgId, {});
            }
        });
    }

    const needsOrg = cUser.orgId && cUser.orgId !== 'undefined' && cUser.orgId !== 'null' && (
        (learnPracticeFlows && learnPracticeFlows.lastOrgId !== cUser.orgId && !learnPracticeFlows.isLoading) ||
        (buildPracticeFlows && buildPracticeFlows.lastOrgId !== cUser.orgId && !buildPracticeFlows.isLoading) ||
        (masterGroupMapper && masterGroupMapper.lastOrgId !== cUser.orgId && !masterGroupMapper.isLoading)
    );

    if (needsOrg || inFlightRequests.has(`ORG_GROUPS_${cUser.orgId}`)) {
        return fetchOrgData(dispatch, api, cUser.orgId, props);
    }

    return Promise.resolve();
};

export const ensureFlowsLoaded = (dispatch, api, cUser, props) => {
    if (!api || !cUser || !props) return Promise.resolve();

    const { learnPracticeFlows, buildPracticeFlows } = props;

    const needsGlobal = (
        (!learnPracticeFlows || (!learnPracticeFlows.fetched && !learnPracticeFlows.isLoading)) ||
        (!buildPracticeFlows || (!buildPracticeFlows.fetched && !buildPracticeFlows.isLoading))
    );

    const isGlobalInFlight = inFlightRequests.has('GLOBAL_LEARN') || inFlightRequests.has('GLOBAL_BUILD');

    if (needsGlobal || isGlobalInFlight) {
        return fetchGlobalFlows(dispatch, api, props).then(() => {
            if (cUser.orgId && cUser.orgId !== 'undefined' && cUser.orgId !== 'null') {
                return fetchOrgFlows(dispatch, api, cUser.orgId, {});
            }
        });
    }

    const needsOrg = cUser.orgId && cUser.orgId !== 'undefined' && cUser.orgId !== 'null' && (
        (learnPracticeFlows && learnPracticeFlows.lastOrgId !== cUser.orgId && !learnPracticeFlows.isLoading) ||
        (buildPracticeFlows && buildPracticeFlows.lastOrgId !== cUser.orgId && !buildPracticeFlows.isLoading)
    );

    if (needsOrg || inFlightRequests.has(`ORG_LEARN_${cUser.orgId}`)) {
        return fetchOrgFlows(dispatch, api, cUser.orgId, props);
    }

    return Promise.resolve();
};
